import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { FiGift, FiCheck, FiX, FiClock, FiUser } = FiIcons

const RewardRequestNotifications = ({ showAll = false, maxDisplay = 3 }) => {
  const { family } = useAuth()
  const { redeemReward, getChildPoints } = useFamily()
  const [requests, setRequests] = useState([])

  useEffect(() => {
    loadRequests()
    // Poll for new requests every 5 seconds
    const interval = setInterval(loadRequests, 5000)
    return () => clearInterval(interval)
  }, [family?.children])

  const loadRequests = () => {
    try {
      const allRequests = JSON.parse(localStorage.getItem('kiosk_reward_requests') || '[]')
      const familyRequests = allRequests.filter(req => 
        family?.children?.some(child => child.id === req.childId)
      )
      setRequests(familyRequests)
    } catch (error) {
      console.error('Error loading reward requests:', error)
      setRequests([])
    }
  }

  const handleApprove = async (requestId) => {
    try {
      const request = requests.find(r => r.id === requestId)
      if (!request) {
        toast.error('Request not found')
        return
      }

      // Check if child still has enough points
      const currentPoints = getChildPoints(request.childId)
      if (currentPoints < request.reward.points_required) {
        toast.error(`${request.childName} doesn't have enough points for this reward anymore`)
        return
      }

      // Redeem the reward in the database
      const result = await redeemReward(request.reward.id, request.childId)
      if (result.error) {
        toast.error(`Error approving reward: ${result.error.message}`)
        return
      }

      // Update localStorage status
      const allRequests = JSON.parse(localStorage.getItem('kiosk_reward_requests') || '[]')
      const updatedRequests = allRequests.map(req =>
        req.id === requestId ? { ...req, status: 'approved', approvedAt: new Date().toISOString() } : req
      )
      localStorage.setItem('kiosk_reward_requests', JSON.stringify(updatedRequests))
      setRequests(prev => prev.map(req =>
        req.id === requestId ? { ...req, status: 'approved', approvedAt: new Date().toISOString() } : req
      ))
      
      toast.success(`✅ Approved ${request.reward.title} for ${request.childName}! Points have been deducted.`)
    } catch (error) {
      console.error('Error approving request:', error)
      toast.error('Error approving request')
    }
  }

  const handleDeny = (requestId) => {
    try {
      const allRequests = JSON.parse(localStorage.getItem('kiosk_reward_requests') || '[]')
      const updatedRequests = allRequests.map(req =>
        req.id === requestId ? { ...req, status: 'denied', deniedAt: new Date().toISOString() } : req
      )
      localStorage.setItem('kiosk_reward_requests', JSON.stringify(updatedRequests))
      setRequests(prev => prev.map(req =>
        req.id === requestId ? { ...req, status: 'denied', deniedAt: new Date().toISOString() } : req
      ))
      
      const request = requests.find(r => r.id === requestId)
      toast.success(`❌ Denied ${request?.reward?.title} for ${request?.childName}`)
    } catch (error) {
      console.error('Error denying request:', error)
      toast.error('Error denying request')
    }
  }

  const clearProcessedRequests = () => {
    try {
      const allRequests = JSON.parse(localStorage.getItem('kiosk_reward_requests') || '[]')
      const activeRequests = allRequests.filter(req => req.status === 'pending')
      localStorage.setItem('kiosk_reward_requests', JSON.stringify(activeRequests))
      setRequests(activeRequests)
      toast.success('Processed requests cleared')
    } catch (error) {
      console.error('Error clearing requests:', error)
      toast.error('Error clearing requests')
    }
  }

  const pendingRequests = requests.filter(req => req.status === 'pending')
  const processedRequests = requests.filter(req => req.status !== 'pending')
  const displayRequests = showAll ? pendingRequests : pendingRequests.slice(0, maxDisplay)

  if (requests.length === 0) {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-orange-500" />
              Pending Reward Requests
              <Badge variant="warning" size="sm">{pendingRequests.length}</Badge>
            </h4>
            {!showAll && pendingRequests.length > maxDisplay && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {maxDisplay} of {pendingRequests.length}
              </span>
            )}
          </div>

          <div className="space-y-3">
            {displayRequests.map((request) => {
              const currentPoints = getChildPoints(request.childId)
              const canAfford = currentPoints >= request.reward.points_required

              return (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 border rounded-lg ${
                    canAfford 
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{request.reward?.emoji || '🎁'}</div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {request.childName} wants "{request.reward?.title}"
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Cost: {request.reward?.points_required} points • 
                          Current Points: {currentPoints} •
                          Requested: {new Date(request.requestedAt).toLocaleString()}
                        </div>
                        {!canAfford && (
                          <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                            ⚠️ Not enough points! Need {request.reward?.points_required - currentPoints} more.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleApprove(request.id)}
                        disabled={!canAfford}
                      >
                        <SafeIcon icon={FiCheck} className="text-sm" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeny(request.id)}
                      >
                        <SafeIcon icon={FiX} className="text-sm" />
                        Deny
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Decisions (only show if showAll is true) */}
      {showAll && processedRequests.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Recent Decisions</h4>
            <Button variant="ghost" size="sm" onClick={clearProcessedRequests}>
              Clear Processed
            </Button>
          </div>

          <div className="space-y-2">
            {processedRequests.slice(-5).map((request) => (
              <div
                key={request.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  request.status === 'approved'
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-lg">{request.reward?.emoji || '🎁'}</div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {request.childName} • {request.reward?.title}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(request.requestedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <Badge
                  variant={request.status === 'approved' ? 'success' : 'alert'}
                  size="sm"
                >
                  {request.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default RewardRequestNotifications